import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api, { extractError } from "@/lib/api";
import { KPICard } from "@/components/KPICard";
import { SectionHeader } from "@/components/SectionHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatCompactINR, formatNumber, formatPercent, ROLE_LABEL } from "@/lib/format";
import { periodPresets, presetToPeriod } from "@/lib/periods";
import { cn } from "@/lib/utils";
import {
  BadgeIndianRupee, TrendingUp, Users2, Wallet, Target as TargetIcon,
  UserPlus, Repeat2, ClipboardCheck, ClipboardX, Clock, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Card } from "@/components/ui/card";

const CHART_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function ChartCard({ title, children, description, testid }) {
  return (
    <Card data-testid={testid} className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="label-caps">{title}</div>
          {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      <div className="h-64 min-h-[16rem] w-full">{children}</div>
    </Card>
  );
}

// Chart style presets — module-scope so their references are stable across
// re-renders (avoids the "new object every render" prop churn ESLint flags).
const CHART_MARGIN = { top: 8, right: 12, left: -12, bottom: 0 };
const CHART_TOOLTIP_STYLE = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
};
const CHART_LEGEND_STYLE = { fontSize: 12 };
const CHART_DOT_SMALL = { r: 3 };
const CHART_DOT_ACTIVE = { r: 5 };
const CHART_BAR_RADIUS = [6, 6, 0, 0];

export default function DashboardPage() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [teamRank, setTeamRank] = useState([]);
  const [mgrRank, setMgrRank] = useState([]);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    setError(null);
    try {
      // Critical calls first — the page cannot render without them.
      const [k, t, l] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/trend?days=14"),
        api.get("/dashboard/leaderboard"),
      ]);
      setKpis(k.data);
      setTrend(t.data);
      setLeaders(l.data);
      // Optional secondary calls — swallow their errors, they just hide widgets.
      const [tr, mr] = await Promise.all([
        api.get("/dashboard/team-ranking").catch(() => ({ data: [] })),
        api.get("/dashboard/manager-ranking").catch(() => ({ data: [] })),
      ]);
      setTeamRank(tr.data);
      setMgrRank(mr.data);
    } catch (e) {
      setError(extractError(e));
      toast.error("Could not load dashboard — please retry");
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (error && !kpis) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4" data-testid="dashboard-error">
        <div className="text-sm text-muted-foreground">Dashboard failed to load — {error}</div>
        <Button variant="secondary" onClick={loadDashboard} data-testid="dashboard-retry">Retry</Button>
      </div>
    );
  }
  if (!kpis) {
    return (
      <div className="flex h-96 items-center justify-center text-muted-foreground">Loading dashboard…</div>
    );
  }

  const trendData = trend.map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
    depositL: +(d.deposit / 100000).toFixed(2),
  }));

  const clientSplit = [
    { name: "New", value: kpis.clients_split.new_deposit || 0 },
    { name: "Existing", value: kpis.clients_split.existing_deposit || 0 },
  ];

  const approvalData = [
    { name: "Pending", value: kpis.approvals.pending, tone: "hsl(var(--warning))" },
    { name: "Approved", value: kpis.approvals.approved, tone: "hsl(var(--success))" },
    { name: "Rejected", value: kpis.approvals.rejected, tone: "hsl(var(--danger))" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <SectionHeader
        title={`${ROLE_LABEL[user.role]} Dashboard`}
        description={`Welcome back, ${user.name.split(" ")[0]}. Here's your live performance snapshot.`}
      />

      {/* Period Explorer */}
      <PeriodExplorer />

      {/* Employees KPI row (admin only) */}
      {kpis.employees && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard testid="kpi-total-employees" label="Total Employees" value={formatNumber(kpis.employees.total || 0)} icon={Users2} delay={0.02} />
          <KPICard testid="kpi-managers" label="Managers" value={formatNumber(kpis.employees.manager || 0)} icon={Users2} delay={0.05} />
          <KPICard testid="kpi-team-leaders" label="Team Leaders" value={formatNumber(kpis.employees.team_leader || 0)} icon={Users2} delay={0.08} />
          <KPICard testid="kpi-executives" label="Sales Executives" value={formatNumber(kpis.employees.sales_executive || 0)} icon={Users2} delay={0.11} />
        </div>
      )}

      {/* Deposit KPIs */}
      <div>
        <div className="label-caps mb-3">Deposit Performance</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            testid="kpi-deposit-today"
            label="Today's Deposit"
            value={formatCompactINR(kpis.today.deposit)}
            icon={BadgeIndianRupee}
            tone="primary"
          />
          <KPICard
            testid="kpi-deposit-week"
            label="Weekly Deposit"
            value={formatCompactINR(kpis.week.deposit)}
            hint={`Target ${formatCompactINR(kpis.targets.weekly_deposit)} · ${formatPercent(kpis.achievement.weekly_deposit_pct)}`}
            progress={kpis.achievement.weekly_deposit_pct}
            icon={TrendingUp}
          />
          <KPICard
            testid="kpi-deposit-month"
            label="Monthly Deposit"
            value={formatCompactINR(kpis.month.deposit)}
            hint={`Target ${formatCompactINR(kpis.targets.monthly_deposit)} · ${formatPercent(kpis.achievement.monthly_deposit_pct)}`}
            progress={kpis.achievement.monthly_deposit_pct}
            icon={Wallet}
          />
          <KPICard
            testid="kpi-active-clients"
            label="Active Clients (Month)"
            value={formatNumber(kpis.active_clients.monthly)}
            hint={`Week: ${formatNumber(kpis.active_clients.weekly)}`}
            icon={Sparkles}
            tone="success"
          />
        </div>
      </div>

      {/* Account opening + client split */}
      <div>
        <div className="label-caps mb-3">Account Opening Performance</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            testid="kpi-acc-week"
            label="Weekly Accounts"
            value={formatNumber(kpis.week.accounts)}
            hint={`Target ${formatNumber(kpis.targets.weekly_accounts)} · ${formatPercent(kpis.achievement.weekly_accounts_pct)}`}
            progress={kpis.achievement.weekly_accounts_pct}
            icon={UserPlus}
          />
          <KPICard
            testid="kpi-acc-month"
            label="Monthly Accounts"
            value={formatNumber(kpis.month.accounts)}
            hint={`Target ${formatNumber(kpis.targets.monthly_accounts)} · ${formatPercent(kpis.achievement.monthly_accounts_pct)}`}
            progress={kpis.achievement.monthly_accounts_pct}
            icon={TargetIcon}
          />
          <KPICard
            testid="kpi-new-clients"
            label="New Client Deposit"
            value={formatCompactINR(kpis.clients_split.new_deposit)}
            hint={`${kpis.clients_split.new_count} txns`}
            icon={UserPlus}
          />
          <KPICard
            testid="kpi-existing-clients"
            label="Existing Client Deposit"
            value={formatCompactINR(kpis.clients_split.existing_deposit)}
            hint={`${kpis.clients_split.existing_count} txns`}
            icon={Repeat2}
          />
        </div>
      </div>

      {/* Approvals */}
      <div>
        <div className="label-caps mb-3">Approvals</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KPICard testid="kpi-pending" label="Pending" value={formatNumber(kpis.approvals.pending)} icon={Clock} tone="warning" />
          <KPICard testid="kpi-approved" label="Approved" value={formatNumber(kpis.approvals.approved)} icon={ClipboardCheck} tone="success" />
          <KPICard testid="kpi-rejected" label="Rejected" value={formatNumber(kpis.approvals.rejected)} icon={ClipboardX} tone="danger" />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Deposit Trend (14d)" description="Approved deposits in ₹ lakhs" testid="chart-deposit-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData} margin={CHART_MARGIN}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                formatter={(v) => [`₹${v} L`, "Deposit"]}
              />
              <Line type="monotone" dataKey="depositL" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={CHART_DOT_SMALL} activeDot={CHART_DOT_ACTIVE} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Accounts Opened (14d)" description="Daily approved account openings" testid="chart-accounts-trend">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={CHART_MARGIN}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="accounts" fill="hsl(var(--chart-2))" radius={CHART_BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="New vs Existing (₹ Deposits, Month)" testid="chart-client-split">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={clientSplit} innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={2}>
                {clientSplit.map((entry) => (
                  <Cell key={`cell-${entry.name}`} fill={CHART_COLORS[entry.name === "New" ? 0 : 1]} />
                ))}
              </Pie>
              <Legend iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                formatter={(v) => formatCompactINR(v)}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Approvals Overview" testid="chart-approvals">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={approvalData} margin={CHART_MARGIN}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="value" radius={CHART_BAR_RADIUS}>
                {approvalData.map((d) => <Cell key={`bar-${d.name}`} fill={d.tone} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Leaderboard + rankings */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card data-testid="leaderboard-card" className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="label-caps">Top Performers (Month)</div>
                <p className="mt-1 text-xs text-muted-foreground">Executives ranked by approved deposits</p>
              </div>
            </div>
            {leaders.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No data yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {leaders.slice(0, 8).map((l, i) => (
                  <div key={l.user_id} className="flex items-center gap-4 py-3">
                    <div className="metric-value w-8 text-lg text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1 truncate text-sm font-medium">{l.user_name}</div>
                    <div className="metric-value text-sm">{formatCompactINR(l.deposit)}</div>
                    <div className="w-16 text-right text-xs text-muted-foreground tabular-nums">{formatNumber(l.accounts)} accts</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </motion.div>

        {teamRank.length > 0 && (
          <RankingCard
            testid="team-ranking-card"
            title="Team Leader Ranking (Month)"
            description="Aggregated across each TL's executives"
            rows={teamRank}
          />
        )}

        {mgrRank.length > 0 && (
          <RankingCard
            testid="manager-ranking-card"
            title="Manager Ranking (Month)"
            description="Aggregated across each manager's full downline"
            rows={mgrRank}
          />
        )}
      </div>
    </div>
  );
}

function RankingCard({ title, description, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.deposit || 0));
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
    >
      <Card data-testid={testid} className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="label-caps">{title}</div>
            {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No data yet.</div>
        ) : (
          <ul className="space-y-3">
            {rows.map((r, i) => {
              const pct = Math.max(4, (r.deposit / max) * 100);
              return (
                <li key={r.id} data-testid={`${testid}-row-${i}`} className="space-y-1.5">
                  <div className="flex items-center gap-3">
                    <div className="metric-value w-6 text-sm text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1 truncate text-sm font-medium">{r.name}</div>
                    <div className="w-14 text-right text-xs text-muted-foreground tabular-nums">team {r.team_size}</div>
                    <div className="metric-value w-24 text-right text-sm">{formatCompactINR(r.deposit)}</div>
                  </div>
                  <div className="ml-9 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </motion.div>
  );
}



function PeriodExplorer() {
  const presets = useMemo(() => periodPresets(), []);
  const [period, setPeriod] = useState(() => presetToPeriod(presets.this_week));
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("this_week");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/reports/period", {
        params: {
          start_date: period.start_date,
          end_date: period.end_date,
          granularity: period.granularity,
        },
      })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [period.start_date, period.end_date, period.granularity]);

  const selectTab = (key) => {
    setTab(key);
    setPeriod(presetToPeriod(presets[key]));
  };

  const onPickerChange = (p) => {
    setTab(""); // clears tab highlight when using a custom pick
    setPeriod(p);
  };

  const tabs = [
    { key: "this_week", label: "Current Week" },
    { key: "this_month", label: "Current Month" },
    { key: "last_week", label: "Last Week" },
    { key: "last_month", label: "Last Month" },
  ];

  return (
    <Card data-testid="period-explorer" className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="label-caps">Period Explorer</div>
          <p className="mt-1 text-xs text-muted-foreground">
            Snapshot of any calendar week or month — data is retained permanently.
          </p>
        </div>
        <PeriodPicker value={period} onChange={onPickerChange} testid="dash-period" />
      </div>

      <div className="mb-5 flex flex-wrap gap-1.5 border-b border-border pb-3">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`dash-tab-${t.key}`}
            onClick={() => selectTab(t.key)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
            )}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums">
          <span className="rounded-md bg-muted px-2 py-1">{period.label}</span>
        </div>
      </div>

      {!data ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <MiniStat label="Deposit Target" value={formatCompactINR(data.deposit_target)} tone="muted" testid="pe-deposit-target" />
          <MiniStat label="Deposit Achieved" value={formatCompactINR(data.deposit_achieved)} tone="primary" testid="pe-deposit-achieved" />
          <MiniStat label="Accounts Target" value={formatNumber(Math.round(data.account_target))} tone="muted" testid="pe-account-target" />
          <MiniStat label="Accounts Opened" value={formatNumber(data.accounts_opened)} tone="primary" testid="pe-accounts-opened" />
          <MiniStat label="Deposit %" value={formatPercent(data.deposit_achievement_pct)} tone={data.deposit_achievement_pct >= 100 ? "success" : "default"} testid="pe-deposit-pct" />
          <MiniStat label="Accounts %" value={formatPercent(data.account_achievement_pct)} tone={data.account_achievement_pct >= 100 ? "success" : "default"} testid="pe-account-pct" />
          <MiniStat label="Active Clients" value={formatNumber(data.active_clients)} tone="success" testid="pe-active" />
          <MiniStat label="New Clients" value={formatNumber(data.new_clients)} testid="pe-new" />
          <MiniStat label="Existing Clients" value={formatNumber(data.existing_clients)} testid="pe-existing" />
          <MiniStat label="Pending" value={formatNumber(data.pending_count)} tone="warning" testid="pe-pending" />
          <MiniStat label="Approved" value={formatCompactINR(data.approved_deposit)} tone="success" hint={`${data.approved_count} txns`} testid="pe-approved" />
          <MiniStat label="Rejected" value={formatCompactINR(data.rejected_deposit)} tone="danger" hint={`${data.rejected_count} txns`} testid="pe-rejected" />
        </div>
      )}
    </Card>
  );
}

function MiniStat({ label, value, tone = "default", hint, testid }) {
  const toneCls = {
    default: "text-foreground",
    primary: "text-primary",
    success: "text-[hsl(var(--success))]",
    warning: "text-[hsl(var(--warning))]",
    danger: "text-[hsl(var(--danger))]",
    muted: "text-muted-foreground",
  }[tone];
  return (
    <div data-testid={testid} className="rounded-lg border border-border bg-background p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={cn("metric-value mt-1.5 text-lg tabular-nums", toneCls)}>{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">{hint}</div>}
    </div>
  );
}
